import { lazy, Suspense, useEffect, useState } from "react";
import { traderPath, fromTraderSlug } from "@/lib/traderSlug";
import { isViewingAsSomeoneElse } from "@/lib/viewAs";
import { useTranslation } from "react-i18next";
import { useState as useReferralState } from "react";
import { readPendingReferral, savePendingReferral } from "@/lib/referral";
// Build tag (forces hash bump): 2026-05-25T2130-bust-cf-cache
const __BUILD_TAG__ = "2026-05-25T2130-bust-cf-cache";
if (typeof window !== "undefined") (window as any).__BUILD_TAG__ = __BUILD_TAG__;
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation } from "react-router-dom";
import { ClerkProvider, SignIn, SignUp, SignedIn, SignedOut } from "@clerk/clerk-react";
import { CLERK_VARIABLES, CLERK_WINDOW_VARIABLES } from "@/lib/clerkTheme";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { LogoOrb } from "@/components/LogoOrb";
import { ScrollToTop } from '@/components/ScrollToTop';
import { CustomizationProvider } from "@/contexts/CustomizationContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { MainLayout } from "./components/layout/MainLayout";
import { CLERK_PUBLISHABLE_KEY } from "@/integrations/clerk/client";
import { motion, AnimatePresence } from "motion/react";

/****************
 * Page components. Dashboard and NotFound eager; every other page loaded when
 * opened (2026-10-20, Guillaume: rendering slow -- one 2.8 MB bundle held
 * every page, the admin's 150 KB among them, before the first paint).
 *
 * The failure that made this monolithic on 2026-05-25 (below) is handled at
 * its cause now: a tab left open across a deploy asks for a chunk that no
 * longer exists, and main.tsx's vite:preloadError handler reloads it once onto
 * the new build instead of leaving it broken. nginx answers a missing
 * /assets/ file with 404, never index.html.
 *
 * History -- eager imports (2026-05-25).
 *
 * Was lazy()'d for code-splitting, but dynamic imports kept failing in users'
 * tabs with "text/html is not a valid JavaScript MIME type" / "Failed to fetch
 * dynamically imported module" whenever a deploy invalidated cached chunk
 * hashes. Chrome's per-tab module map sticky-fails the URL on first error
 * and the only fix was killing the tab. Going monolithic eliminates the
 * entire failure mode — the trade-off is ~600kb of upfront JS instead of
 * per-route loads, which is fine for our bundle size and user count.
 ****************/

import Dashboard from "./pages/Dashboard";
const ProfileNew = lazy(() => import("./pages/ProfileNew"));
const GroupProfile = lazy(() => import("./pages/GroupProfile"));
const Assets = lazy(() => import("./pages/Assets"));
const VirginDashboard = lazy(() => import("./pages/VirginDashboard"));
const TradingNew = lazy(() => import("./pages/TradingNew"));
const LearningCenter = lazy(() => import("./pages/LearningCenter"));
const LearnHub = lazy(() => import("./components/engagement/Learn").then((m) => ({ default: m.LearnHub })));
const LearnArticles = lazy(() => import("./components/engagement/Learn").then((m) => ({ default: m.LearnArticles })));
const LearnArticle = lazy(() => import("./components/engagement/Learn").then((m) => ({ default: m.LearnArticle })));
const LessonPage = lazy(() => import("./components/engagement/Lessons").then((m) => ({ default: m.LessonPage })));
const LearnQuizzes = lazy(() => import("./components/engagement/Learn").then((m) => ({ default: m.LearnQuizzes })));
const LearnQuiz = lazy(() => import("./components/engagement/Learn").then((m) => ({ default: m.LearnQuiz })));
const GetStarted = lazy(() => import("./pages/GetStarted"));
const Activity = lazy(() => import("./pages/Activity"));
const SessionHistory = lazy(() => import("./pages/SessionHistory"));
const AIConnections = lazy(() => import("./pages/AIConnections"));
const AIHub = lazy(() => import("./pages/AIHub"));
const AIMiroFish = lazy(() => import("./pages/AIMiroFish"));
const AITensor = lazy(() => import("./pages/AITensor"));
const AIHermes = lazy(() => import("./pages/AIHermes"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const Community = lazy(() => import("./pages/Community"));
const AdminActivity = lazy(() => import("./pages/AdminActivity"));
const SessionDetails = lazy(() => import("./pages/SessionDetails"));
import NotFound from "./pages/NotFound";
const Legal = lazy(() => import("./pages/Legal"));
const Competition = lazy(() => import("./pages/Competition"));
const CompetitionProfile = lazy(() => import("./pages/CompetitionProfile"));
const DeleteAccount = lazy(() => import("./pages/legal/DeleteAccount"));

const queryClient = new QueryClient();

/****************
 * Page loading fallback for lazy loaded components
 ****************/

/*
 * /profile/<trader> is the address from before the page was called Traders.
 * The redirect was written <Navigate to="/traders/:traderId">, and Navigate
 * does not fill a pattern in: every old link landed on the literal address
 * "/traders/:traderId", a page with no trader on it. 2026-10-01: it carries
 * the trader across, and the Traders page then moves the address to the
 * trader's name (traderSlug.ts).
 */
/****************
 * A PHONE ON THE WEB GOES TO /m, ON EVERY ROUTE CHANGE, NOT ONLY ON LOAD.
 *
 * index.html sends a phone to /m before the app loads, but skips /auth (Clerk
 * finishes sign-in there). Signing in then moves to / inside the app, with no
 * page load, so the phone stayed on the web. Guillaume, 2026-10-09: "I just
 * connected on my phone and it did not". Same rules: window.copyfiToPhone.
 ****************/
function PhoneRedirect() {
  const { pathname } = useLocation();
  useEffect(() => {
    (window as unknown as { copyfiToPhone?: () => void }).copyfiToPhone?.();
  }, [pathname]);
  return null;
}

/****************
 * THE PRIVATE PREVIEW. Guillaume, 2026-10-21: "I want to see it all before it
 * goes live to users."
 *
 * A bundle can be built a second time under /preview (scripts/build-preview.mjs:
 * vite --base /preview/, VITE_PREVIEW=1) and served beside the live app. In
 * that build the router lives under /preview, a ribbon says so on every
 * screen, and anyone who is not an admin is sent to the live app. In the live
 * build BASE_URL is "/" and VITE_PREVIEW is unset: nothing here does anything.
 ****************/
const IS_PREVIEW = import.meta.env.VITE_PREVIEW === "1";
const ROUTER_BASE = import.meta.env.BASE_URL.replace(/\/$/, "") || undefined;

function PreviewGate() {
  const { isRealAdmin: isAdmin, isLoading } = useAuth();
  useEffect(() => {
    // Looking through a user's eyes (?viewAs, from the admin's Open their screens)
    // is never an admin by design (AuthContext isRealAdmin), so the gate let it
    // through rather than sending the admin to the live app (review #29, 10-25).
    if (IS_PREVIEW && !isLoading && !isAdmin && !isViewingAsSomeoneElse()) window.location.replace("/");
  }, [isAdmin, isLoading]);
  if (!IS_PREVIEW) return null;
  return (
    <div className="fixed top-0 left-1/2 -translate-x-1/2 z-[9999] rounded-b-md bg-warning px-3 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-black shadow pointer-events-none">
      Preview, not live
    </div>
  );
}

function LegacyProfileRedirect() {
  const { traderId } = useParams<{ traderId: string }>();
  // An old link may carry the venue id: forward it opaque, never in the clear (10-25).
  const v = fromTraderSlug(traderId || '');
  // A real name stays a name; an id goes out opaque (traderPath's own fallback).
  return <Navigate to={traderPath(v, v)} replace />;
}

function PageLoadingFallback() {
  const { t } = useTranslation("common");
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-terminal">
      <div className="text-center animate-fade-in">
        <div className="relative w-16 h-16 mx-auto mb-4">
          <div className="absolute inset-0 rounded-full border-2 border-primary/20" />
          <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-primary animate-spin" />
          <div className="absolute inset-2 rounded-full border-2 border-transparent border-b-accent-purple animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
        </div>
        <p className="text-sm text-muted-foreground font-medium">{t("auth.loading")}</p>
      </div>
    </div>
  );
}

/****************
 * Feature data for the welcome page
 ****************/

/****************
 * HOW COPY-TRADING WORKS, IN THREE STEPS.
 *
 * This was four feature blurbs of equal weight -- Copy Top Traders, Live
 * Analytics, Strategy Builder, Secure & Private -- which is a list of nouns. A
 * visitor who has never seen a copy-trading product still could not say what
 * happens after they sign up, and Guillaume's own visitor said the perks had to
 * land "within the very first 3 seconds, otherwise they will not bother to look
 * into".
 *
 * Features answer "what does it have". A newcomer is asking "what happens to my
 * money", and the answer is a sequence: choose someone, set your limits, their
 * trades appear in your account. Numbering it says there is an order and that
 * it ends somewhere, which four bullets cannot.
 *
 * Three, not four. The fourth thing that mattered -- that we never hold the
 * money -- is a promise, not a step, and it now sits under the sequence where a
 * promise belongs rather than competing with it as a peer.
 ****************/
const STEPS = [
  {
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      </svg>
    ),
    title: "welcome.step1.title",
    desc: "welcome.step1.desc",
  },
  {
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3" />
        <path d="M1 14h6M9 8h6M17 16h6" />
      </svg>
    ),
    title: "welcome.step2.title",
    desc: "welcome.step2.desc",
  },
  {
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
      </svg>
    ),
    title: "welcome.step3.title",
    desc: "welcome.step3.desc",
  },
];


/****************
 * The orb, moving enough to notice.
 *
 * The first attempt at this drifted 3px over 4.8s. That is 1.25px per second on
 * an 80px mark -- technically animating, visually static, and reported as "I
 * don't see motion". Correctly: below about 4px/s at this size nothing reads as
 * movement, it reads as a still image.
 *
 * So the motion is now carried by a ROTATION, which is the one thing that never
 * looks paused. A drift and a pulse both spend most of their cycle near the
 * turnaround, moving slowest exactly when someone glances at them; a sheen
 * sweeping around the orb is in motion at every instant of its loop.
 *
 * Three layers, three periods -- 9s, 3.2s, 4s. None divides evenly into
 * another, so the combination does not visibly repeat.
 *
 *   sheen   a conic highlight rotating once every 9s, behind the mark
 *   glow    a halo breathing 0.35 -> 0.95 and 0.9 -> 1.3 over 3.2s
 *   float   the mark itself bobbing 7px over 4s -- 3.5px/s, at the threshold
 *           rather than under it, and it no longer carries the job alone
 *
 * Still restrained on purpose: this sits inches from a sign-in form. The test
 * is that it is noticed in peripheral vision and never asks to be watched.
 *
 * Everything is transform and opacity, so it composites on the GPU and cannot
 * trigger layout on a page whose height took several commits to fit.
 ****************/

/****************
 * Clerk appearance - dark theme with proper contrast
 ****************/

const clerkAppearance = {
  layout: {
    unsafe_disableDevelopmentModeWarnings: true,
  },
  variables: CLERK_VARIABLES,
  elements: {
    rootBox: { width: "100%" },
    cardBox: { width: "100%" },
    card: {
      background: "transparent",
      boxShadow: "none",
      border: "none",
      padding: 0,
      gap: "0.5rem",
      /**
       * The 1px misalignment, measured on production 2026-09-03.
       *
       * Clerk's own card carries `margin: -1px -1px 0` so that ITS 1px border
       * lands on top of the cardBox's ring instead of beside it. We set
       * `border: "none"` above and left the compensation behind, so the card
       * became 2px WIDER than the box it sits in and hung 1px off each side:
       * .cl-cardBox 781->1171, .cl-card and everything inside it 780->1172,
       * while our own Sign In / Sign Up toggle sat at the true 781->1171.
       *
       * Every element in the card inherited that shift, which is why the
       * Google button, the email field and Continue all overhung the rows
       * above them by a hair -- enough to read as crooked without being
       * obviously broken. Verified after the fix: toggle, cardBox, card,
       * social button, input and submit all report 781->1171 exactly.
       *
       * If the border is ever restored, this must go back to -1px with it.
       */
      margin: 0,
      width: "100%",
    },
    headerTitle: { display: "none" },
    headerSubtitle: { display: "none" },
    main: {
      gap: "1.25rem",
    },
    form: {
      background: "transparent",
      border: "none",
      boxShadow: "none",
      padding: 0,
    },
    formContainer: {
      background: "transparent",
      border: "none",
      boxShadow: "none",
      padding: 0,
    },
    socialButtonsBlockButton: {
      /* Outlined, not filled. A filled block beside flat inputs reads as
         already-chosen, which is what "seem highlighted" meant -- the eye takes
         a raised surface for a selected state. An outline offers the choice
         without claiming it has been made. */
      background: "transparent",
      border: "1px solid rgba(255,255,255,0.12)",
      color: "#e8ecf4",
      borderRadius: "0px",
      padding: "0.75rem 1rem",
      position: "relative",
    },
    socialButtonsBlockButton__hover: {
      background: "rgba(255,255,255,0.04)",
    },
    socialButtonsBlockButtonText: {
      color: "#e8ecf4",
      fontSize: "0.875rem",
    },
    socialButtonsProviderIcon__google: {
      /* ensure icon stays in flow */
    },
    badge: {
      display: "none",
    },
    /**
     * The "Last used" chip. It is a `Span`, not a button -- Clerk renders it
     * from the `lastAuthenticationStrategy` localization key purely as a label
     * -- so clicking it does nothing, which is exactly what Guillaume reported.
     *
     * In this theme it reads as one: borderRadius is 0 everywhere, so a small
     * bordered chip beside a field looks like a disabled control sitting next
     * to an input. Hidden rather than restyled, matching the `badge` rule above
     * that was already meant to cover it -- `badge` is a DIFFERENT descriptor
     * (Primary / Unverified / plan chips), which is why setting it never
     * touched this one.
     *
     * Nothing is lost by hiding it: Clerk already prefills the identifier field
     * with the last-used account, so the information it carried is the state of
     * the form itself.
     */
    lastAuthenticationStrategyBadge: {
      display: "none",
    },
    formFieldLabel: {
      color: "#c0c8d8",
      fontSize: "0.8125rem",
    },
    formFieldInput: {
      background: "#14161b",
      border: "1px solid rgba(255,255,255,0.1)",
      color: "#e8ecf4",
      borderRadius: "0px",
      fontSize: "0.875rem",
      padding: "0.7rem 0.85rem",
    },
    formFieldInput__identifier: {
      fontSize: "0.875rem",
      padding: "0.7rem 0.85rem",
    },
    formButtonPrimary: {
      background: "linear-gradient(135deg, #3b82f6, #7c3aed)",
      color: "#ffffff",
      borderRadius: "0px",
      fontWeight: "600",
      padding: "0.7rem 1rem",
    },
    footerAction: {
      color: "#8a94a6",
    },
    footerActionLink: {
      color: "#3b82f6",
    },
    dividerLine: {
      background: "rgba(255,255,255,0.08)",
    },
    dividerRow: {
      margin: "0.25rem 0",
    },
    dividerText: {
      color: "#8a94a6",
    },
    formFieldRow: {
      marginBottom: "0.25rem",
    },
    identityPreviewEditButton: {
      color: "#3b82f6",
    },
    identityPreviewText: {
      color: "#e8ecf4",
    },
    formFieldInputShowPasswordButton: {
      color: "#8a94a6",
    },
    otpCodeFieldInput: {
      background: "#14161b",
      border: "1px solid rgba(255,255,255,0.1)",
      color: "#e8ecf4",
    },
    formResendCodeLink: {
      color: "#3b82f6",
    },
    alert: {
      background: "rgba(239,68,68,0.1)",
      border: "1px solid rgba(239,68,68,0.2)",
      color: "#fca5a5",
    },
    alertText: {
      color: "#fca5a5",
    },
    footer: {
      display: "none",
    },
  },
} as const;

/****************
 * CopyFi Welcome Page - wraps around Clerk sign-in/sign-up
 ****************/

function WelcomePage({ mode = "signin" }: { mode?: "signin" | "signup" }) {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const authMode = mode;
  const setAuthMode = (next: "signin" | "signup") => {
    navigate(next === "signup" ? "/auth/signup" : "/auth");
  };

  // Workaround: Clerk's OTP container intercepts clicks instead of delegating to the inner input.
  // Forward any click on .cl-otpCodeFieldInputContainer to the input it wraps.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const container = target.closest('.cl-otpCodeFieldInputContainer') as HTMLElement | null;
      if (container) {
        const input = container.querySelector('input') as HTMLInputElement | null;
        input?.focus();
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="fixed inset-0 bg-background" />

      {/****************
        * THE CANYON.
        *
        * "Auth page still have too much empty space." Measured at 1554x800
        * before this: 96px of padding on the left edge, 129px on the right, and
        * 249px of nothing between the two columns -- because both halves were
        * flex-1 across the FULL screen with their content centred inside, so
        * the wider the monitor the further apart they were pushed. Meanwhile
        * the left column was 850px tall in an 800px viewport, so the page
        * managed to overflow and look empty at the same time.
        *
        * Capping the row and centring it puts the columns beside each other
        * instead of at opposite edges: the gap went 249px -> 67px, and the
        * empty space moved to the outer margins where it reads as breathing
        * room rather than a hole in the middle.
        ****************/}
      <div className="relative z-10 min-h-screen w-full max-w-[1180px] mx-auto flex flex-col lg:flex-row">

        {/* Left side: Hero & Features */}
        <div className="flex-none lg:flex-1 flex flex-col justify-center px-6 py-6 lg:px-8 lg:py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="max-w-xl mx-auto lg:mx-0"
          >
            {/****************
              * ONE LOCKUP, NOT TWO STACKED ROWS.
              *
              * "the Orb is too big and I need to scroll down".
              *
              * The orb was w-36/md:w-44 -- 176px -- sitting on its own line
              * ABOVE the wordmark, so the brand alone consumed 239px before the
              * headline began, and step 3 plus the trust line fell below the
              * fold. A hero image on a page with no room for one.
              *
              * Beside the wordmark it is a logo again rather than an
              * illustration, and it costs one row instead of two. Left column
              * 850px -> 592px at a 1554x800 viewport, measured on the live page:
              * the whole thing fits, with headroom for shorter laptops.
              ****************/}
            <div className="flex items-center justify-center lg:justify-start gap-3.5 mb-3.5">
              <img
                src="/copyfi_logo.png"
                alt="CopyFi"
                className="h-11 md:h-12 lg:h-14 object-contain"
              />
              {/* To the RIGHT of the wordmark, on request. The name is what a
                  first-time visitor needs to read, so it takes the position the
                  eye reaches first and the mark trails it. */}
              <LogoOrb />
            </div>

            {/* The promise, as the loudest thing on the page.
                It was 2xl in the MUTED colour and lighter than the paragraph
                under it, so the one line that says what this is read as a
                caption. It is now the headline it always was. */}
            {/* Two blocks, not one wrapping line.
                "Start copying." was inside a span on the same line, so the
                break landed wherever the width ran out -- "Stop watching.
                Start / copying." Guillaume: "Put the Start next to the
                Copying." Each clause is its own block now, so the sentence
                breaks where it means to at every width.

                Brighter than before: blue-400/violet-400 rather than
                primary/violet-500. A gradient is already the lowest-contrast
                way to set type, and the darker pair made the half that carries
                the verb the harder half to read. */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white text-center lg:text-start mb-1.5 leading-[1.15]">
              <span className="block">{t("welcome.headline1")}</span>
              {/* leading-[1.3] on THIS line only, and it is not a spacing choice.
                  bg-clip-text paints the gradient inside the text box and
                  nothing outside it, so a line box shorter than the font's full
                  ink height slices the descenders off. Measured on the live
                  page: 48px type in a 55px box, where the "y" of copying needs
                  about 61px. It was cut, exactly as reported.

                  Proportional rather than a pixel pad, so it holds at all three
                  breakpoints (3xl/4xl/5xl) instead of only the one I checked.
                  The line above keeps the tighter 1.15 -- it has no descender
                  and no gradient, so it has nothing to lose. */}
              <span className="block leading-[1.3] bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
                {t("welcome.headline2")}
              </span>
            </h1>
            <p className="text-base md:text-lg text-foreground/85 text-center lg:text-start mb-5 max-w-lg leading-relaxed">
              {t("welcome.tagline")}
            </p>

            {/* The sequence. Numbered because the order is the explanation:
                a newcomer's question is not "what features" but "what happens
                to my money", and that has a beginning, a middle and an end. */}
            <ol className="hidden sm:block space-y-3.5 max-w-lg">
              {STEPS.map((step, i) => (
                <li key={step.title} className="flex items-start gap-4">
                  <span
                    className="shrink-0 flex items-center justify-center h-8 w-8 rounded-full
                               bg-primary/10 border border-primary/30 text-primary"
                    aria-hidden
                  >
                    {step.icon}
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-base md:text-lg font-semibold text-foreground leading-snug">
                      <span className="text-primary/70 tabular-nums me-1.5">{i + 1}.</span>
                      {t(step.title)}
                    </h3>
                    <p className="text-sm md:text-base text-foreground/70 mt-0.5 leading-relaxed">
                      {t(step.desc)}
                    </p>
                  </div>
                </li>
              ))}
            </ol>

            {/* The promise, under the sequence rather than beside it.
                "We never touch your funds" is the objection every newcomer has
                about handing an app their exchange account, and it was buried
                as the fourth of four equal bullets. It answers a worry, so it
                belongs after the explanation that creates it. */}
            <p className="hidden sm:flex items-start gap-2 mt-4 text-sm text-foreground/70 max-w-lg">
              <svg className="h-4 w-4 shrink-0 text-emerald-500" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              {t("welcome.trust")}
            </p>
          </motion.div>
        </div>

        {/* Right side: Auth panel */}
        <div className="flex-1 flex items-center justify-center px-6 py-6 lg:px-8 lg:py-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="w-full max-w-lg"
          >
            {/* Auth card */}
            <div>
              <div className="glass-card">

                {/* Auth mode toggle */}
                <div className="px-6 pt-6 pb-2">
                  <div className="flex gap-1 p-1 rounded-lg bg-muted/30 border border-border/30">
                    <button
                      onClick={() => setAuthMode("signin")}
                      className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                        authMode === "signin"
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {t("welcome.signIn")}
                    </button>
                    <button
                      onClick={() => setAuthMode("signup")}
                      className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                        authMode === "signup"
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {t("welcome.signUp")}
                    </button>
                  </div>
                </div>

                {/* Clerk component — no AnimatePresence wrapper; it interferes with Clerk's segmented OTP focus management */}
                <div className="px-6 pb-8 pt-2">
                  {authMode === "signin" ? (
                    <SignIn
                      routing="virtual"
                      signUpUrl="/auth/signup"
                      fallbackRedirectUrl="/"
                      appearance={clerkAppearance}
                    />
                  ) : (
                    <SignUp
                      routing="virtual"
                      signInUrl="/auth"
                      fallbackRedirectUrl="/"
                      appearance={clerkAppearance}
                    />
                  )}
                </div>

                {/* The same disclosure the mobile sign-up carries, because the
                    two clients must not tell people different things about
                    where their data goes. Shown on sign-up only: it describes
                    what creating an account agrees to, and repeating it at
                    every sign-in would train people to scroll past it. */}
                {authMode === "signup" && <ReferralCodeField />}
                {authMode === "signup" && (
                  <div className="px-6 pb-1">
                    <p className="text-[11px] leading-relaxed text-muted-foreground/70">
                      {t("welcome.disclosure")}
                    </p>
                    <p className="mt-1 flex flex-wrap gap-x-3 text-[11px]">
                      <a href="/terms" target="_blank" rel="noopener" className="text-primary underline underline-offset-2">{t("legal.terms")}</a>
                      <a href="/privacy" target="_blank" rel="noopener" className="text-primary underline underline-offset-2">{t("legal.privacy")}</a>
                      <a href="/support" target="_blank" rel="noopener" className="text-primary underline underline-offset-2">{t("legal.support")}</a>
                    </p>
                  </div>
                )}

                {/* Bottom branding */}
                <div className="px-6 pb-4 border-t border-border/30">
                  <p className="text-xs text-muted-foreground/50 text-center pt-3">
                    CopyFi
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

/****************
 * Protected route wrapper using Clerk
 ****************/

/* The OAuth consent page (pages/OAuthConsent.tsx). Signed out, the address is
   kept so the sign-in comes back to it rather than to the dashboard: the AI
   app is waiting on the other end. */
const OAUTH_RETURN = 'copyfi.oauthReturn';
function ConsentRoute() {
  return (
    <>
      <SignedIn><OAuthConsent /></SignedIn>
      <SignedOut>
        <RememberAndSignIn />
      </SignedOut>
    </>
  );
}
function RememberAndSignIn() {
  try { sessionStorage.setItem(OAUTH_RETURN, JSON.stringify({ to: window.location.pathname + window.location.search, at: Date.now() })); } catch { /* private mode */ }
  return <Navigate to="/auth" replace />;
}
function OAuthReturn() {
  const navigate = useNavigate();
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(OAUTH_RETURN);
      if (!raw) return;
      sessionStorage.removeItem(OAUTH_RETURN);
      const { to, at } = JSON.parse(raw);
      if (typeof to === 'string' && to.startsWith('/oauth/consent?') && Date.now() - at < 10 * 60 * 1000) navigate(to, { replace: true });
    } catch { /* nothing to return to */ }
  }, [navigate]);
  return null;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return (
    <>
      <SignedIn><OAuthReturn />{children}</SignedIn>
      <SignedOut>
        <Navigate to="/auth" replace />
      </SignedOut>
    </>
  );
}

/****************
 * Main App component
 ****************/

const App = () => {
  if (!CLERK_PUBLISHABLE_KEY) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-terminal">
        <div className="text-center p-8 glass-card rounded-xl">
          <h1 className="text-xl font-bold text-destructive mb-4">Configuration Error</h1>
          <p className="text-muted-foreground">
            Missing VITE_CLERK_PUBLISHABLE_KEY environment variable.
          </p>
          <p className="text-sm mt-2 text-muted-foreground">
            Please add your Clerk publishable key to the .env file.
          </p>
        </div>
      </div>
    );
  }

  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      // Our colours for the windows Clerk opens by itself, like the code check
      // of Change password (lib/clerkTheme.ts). The sign-in card sets its own.
      appearance={{ variables: CLERK_WINDOW_VARIABLES }}
      signInUrl="/auth"
      signUpUrl="/auth/signup"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
    >
      <QueryClientProvider client={queryClient}>
        <CustomizationProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter basename={ROUTER_BASE}>
              <AuthProvider>
                <PreviewGate />
                {/* Inside the router, outside Suspense: it must see every route
                    change, including the ones that suspend on a lazy chunk. */}
                <ScrollToTop />
                <PhoneRedirect />
                {/* NO SCREEN TIMER HERE. MainLayout.tsx:33 already runs one on
                    the same pathname, and it says in its own comment why that is
                    the right place: it renders every route, so a per-page timer
                    cannot be forgotten on the page that turns out to matter.

                    A second timer here ran from 11:05 to 11:40 on 2026-09-24 and
                    double-counted every screen view under the layout -- two
                    events, twice the milliseconds, on every route a signed-in
                    user visits. It was added on the belief that nothing emitted
                    screen_viewed from the web, which was simply false and took
                    one `git grep` to disprove. */}
                <ErrorBoundary>
                <Suspense fallback={<PageLoadingFallback />}>
                  <Routes>
                    <Route path="/auth/signup/*" element={<WelcomePage mode="signup" />} />
                    <Route path="/auth/*" element={<WelcomePage mode="signin" />} />
                    <Route path="/oauth/consent" element={<ConsentRoute />} />
                    {/* Public: the store listings and the sign-up disclosure link
                        here, and a reviewer or a signed-out visitor must reach them. */}
                    <Route path="/privacy" element={<Legal doc="privacy" />} />
                    <Route path="/terms" element={<Legal doc="terms" />} />
                    <Route path="/support" element={<Legal doc="support" />} />
                    {/* The competition's Official Rules: public, like the terms (content/legal.ts). */}
                    <Route path="/competition/rules" element={<Legal doc="competition" />} />
                    <Route path="/competition/reglement" element={<Legal doc="competitionFr" />} />
                    {/* Public: Play's account-deletion URL (pages/legal/DeleteAccount.tsx). */}
                    <Route path="/delete-account" element={<DeleteAccount />} />
                    <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                      <Route index element={<Dashboard />} />
                      <Route path="traders" element={<ProfileNew />} />
                      <Route path="traders/:traderId" element={<ProfileNew />} />
                      <Route path="groups/:setId" element={<GroupProfile />} />
                      {/* The Assets section (2026-10-04): the list, and one asset. */}
                      <Route path="assets" element={<Assets />} />
                      <Route path="assets/:slug" element={<Assets />} />
                      <Route path="configure" element={<VirginDashboard />} />
                      <Route path="copy-trade" element={<TradingNew />} />
                      <Route path="sessions" element={<SessionHistory />} />
                      {/* Admin-only, and the page says so itself rather than
                          rendering nothing for someone who lacks the role. */}
                      <Route path="admin" element={<AdminActivity />} />
                      <Route path="session/:sessionId" element={<SessionDetails />} />
                      {/* Learn opens on Videos, Articles and Quiz (Guillaume 2026-10-22). */}
                      <Route path="learn" element={<LearnHub />} />
                      <Route path="learn/videos" element={<LearningCenter />} />
                      <Route path="learn/articles" element={<LearnArticles />} />
                      <Route path="learn/articles/:id" element={<LearnArticle />} />
                      <Route path="learn/quiz" element={<LearnQuizzes />} />
                      <Route path="learn/quiz/:id" element={<LearnQuiz />} />
                      <Route path="learn/lessons/:id" element={<LessonPage />} />
                      {/* Achievements are a pop-up from the header trophy (Guillaume 10-25); old links go home. */}
                      <Route path="achievements" element={<Navigate to="/" replace />} />
                      <Route path="learn/achievements" element={<Navigate to="/" replace />} />
                      {/* No Groups page: groups and categories live in the Traders table (round 7 #110). */}
                      <Route path="groups" element={<Navigate to="/traders" replace />} />
                      <Route path="get-started" element={<GetStarted />} />
                      <Route path="activity" element={<Activity />} />
                      {/* Connect your own assistant. Not admin-gated: it is
                          the user's own data and their own choice of AI. */}
                      {/* /ai is the section's doors (round 7 #109); the old chooser is /ai/connect. */}
                      <Route path="ai" element={<AIHub />} />
                      <Route path="ai/connect" element={<AIConnections />} />
                      {/* MiroFish, open to every signed-in user (2026-11-02). */}
                      <Route path="ai/mirofish" element={<AIMiroFish />} />
                      {/* The AI section's pages (Guillaume 2026-10-22): Tensor, AI Assistant, AI Trader, MiroFish. */}
                      <Route path="ai/tensor" element={<AITensor />} />
                      <Route path="ai/assistant" element={<AIConnections key="assistant" initialPart="assistant" />} />
                      <Route path="ai/trader" element={<AIConnections key="traders" initialPart="traders" />} />
                      {/* Hermes, open to every signed-in user (2026-11-02). */}
                      <Route path="ai/hermes" element={<AIHermes />} />
                      {/* The Community (2026-09-26). pages/Community.tsx. */}
                      <Route path="community" element={<Community />} />
                      {/* The $1,000 paper-trading competition (pages/Competition.tsx). */}
                      <Route path="competition" element={<Competition />} />
                      {/* One participant's competition, from the ranking (Guillaume, 10-28). */}
                      <Route path="competition/profile/:entryId" element={<CompetitionProfile />} />
                      <Route path="community/:postId" element={<Community />} />

                      {/* Redirects from old routes */}
                      <Route path="profile/:traderId" element={<LegacyProfileRedirect />} />
                      <Route path="profile" element={<Navigate to="/traders" replace />} />
                      <Route path="virgin-dashboard" element={<Navigate to="/copy-trade" replace />} />
                      <Route path="trading-new" element={<Navigate to="/copy-trade" replace />} />
                      <Route path="trading" element={<Navigate to="/copy-trade" replace />} />
                      <Route path="session-history" element={<Navigate to="/sessions" replace />} />
                      <Route path="learning-center" element={<Navigate to="/learn" replace />} />
                    </Route>
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
                </ErrorBoundary>
              </AuthProvider>
            </BrowserRouter>
          </TooltipProvider>
        </CustomizationProvider>
      </QueryClientProvider>
    </ClerkProvider>
  );
};

export default App;

/****************
 * "WHO INVITED YOU?" UNDER THE SIGN-UP FORM. Guillaume, 2026-09-26. Clerk
 * draws the form, so the code sits under it; a ?ref= link fills it in. It is
 * attached to the account the moment it exists (lib/referral.ts).
 ****************/
function ReferralCodeField() {
  const { t } = useTranslation("common");
  const pending = readPendingReferral();
  const [code, setCode] = useReferralState(pending?.code ?? "");
  const fromLink = pending?.source === "link" && pending.code === code;
  return (
    <div className="px-6 pb-3">
      <label className="block text-[12px] text-muted-foreground mb-1" htmlFor="referral-code">
        {t("referral.fieldLabel")}
      </label>
      <input
        id="referral-code"
        value={code}
        onChange={(e) => {
          const v = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 16);
          setCode(v);
          savePendingReferral(v, "code");
        }}
        placeholder={t("referral.placeholder")}
        className="w-full rounded-md border border-border/60 bg-transparent px-3 py-2 text-sm font-mono tracking-wide"
        autoComplete="off"
      />
      {fromLink && <p className="text-[11px] text-success mt-1">{t("referral.fromLink")}</p>}
    </div>
  );
}
